import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUser, updateUser } from '../api/action/user';

const fetchUserIP = async () => {
    try {
        const response = await fetch('https://api.ipify.org?format=json');
        const data = await response.json();
        return data.ip;
    } catch {
        return "unknown";
    }
};

const extractUserFromResponse = (response) => {
    // The API returns { "0": { ...userData }, "personalPreference": ... }
    // OR an array [ { ...userData } ]
    // OR response.payload / response.data

    if (!response) return null;

    // Case 1: response has numeric key "0" (the shape shown in your JSON)
    if (response["0"] && typeof response["0"] === "object") {
        return response["0"];
    }

    // Case 2: array response
    if (Array.isArray(response) && response.length > 0) {
        return response[0];
    }

    // Case 3: wrapped in payload
    if (response.payload) {
        if (Array.isArray(response.payload) && response.payload.length > 0) {
            return response.payload[0];
        }
        if (response.payload["0"]) return response.payload["0"];
        return response.payload;
    }

    // Case 4: wrapped in data
    if (response.data) {
        if (Array.isArray(response.data) && response.data.length > 0) {
            return response.data[0];
        }
        return response.data;
    }

    return null;
};

const EnsurePersonalPreference = () => {
    const authState = useSelector((state) => state?.auth?.data) || {};
    const dispatch = useDispatch();
    const companyState = useSelector((state) => state.companyReducer.company);
    const companyPrimaryId = companyState?.primaryId || '';
    const domainType = companyState?.domain?.type || '';

    // Use a ref so we never run this more than once per session
    const hasRun = useRef(false);

    useEffect(() => {
        // Guard: only run once, and only when we have the required IDs
        if (hasRun.current) return;
        if (!authState?.primaryId || !companyPrimaryId) return;

        // Also skip if already marked done in sessionStorage
        const sessionKey = `personalPrefChecked_${authState.primaryId}`;
        if (sessionStorage.getItem(sessionKey)) return;

        const checkAndUpdate = async () => {
            hasRun.current = true;

            try {
                const payload = {
                    primaryId: authState.primaryId,
                    company: {
                        primaryId: companyPrimaryId,
                        domain: { type: domainType }
                    }
                };

                const response = await dispatch(fetchUser(payload));

                const userData = extractUserFromResponse(response);

                if (!userData) {
                    return;
                }

                // Mark as checked regardless of outcome so we don't keep retrying
                sessionStorage.setItem(sessionKey, 'true');

                const hasPersonalPreference =
                    userData.personalPreference !== null &&
                    userData.personalPreference !== undefined;

                if (hasPersonalPreference) {
                    return;
                }


                const userIP = await fetchUserIP();

                const personalPreference = {
                    communicationPreferences: [],
                    legalRequirements: [
                        {
                            type: "TERMS_AND_CONDITIONS",
                            acceptedDate: new Date().toISOString(),
                            accepted: true,
                            history: [],
                            ipAddress: userIP
                        }
                    ]
                };

                // Build the update payload using the FULL user object
                // so the API has everything it needs and doesn't reject it
                const updatePayload = {
                    ...userData,
                    personalPreference,
                    modifiedBy:
                        authState?.firstName && authState?.lastName
                            ? `${authState.firstName} ${authState.lastName}`
                            : authState?.email || "system"
                };
                // Fire and forget: do not dispatch anything that touches auth state.
                // updateUser already has the dispatch commented out, which is correct
                const editResponse = await dispatch(updateUser(updatePayload));
                if (!editResponse || editResponse.code === 400) {
                    // Remove session flag so it can retry next time
                    sessionStorage.removeItem(sessionKey);
                    hasRun.current = false;
                }

            } catch {
                // Reset so it can try again next session
                hasRun.current = false;
                sessionStorage.removeItem(`personalPrefChecked_${authState.primaryId}`);
            }
        };

        checkAndUpdate();

    }, [
        authState?.email,
        authState?.firstName,
        authState?.lastName,
        authState?.primaryId,
        companyPrimaryId,
        dispatch,
        domainType
    ]);

    return null;
};

export default EnsurePersonalPreference;
